const VERIFY_ROUTE_PREFIX = "/verify/rx";
const VERIFY_CONTEXT_KEY = "afrimed:rx:verify-context:v1";
const RESTORABLE_NAVIGATION_TYPES = new Set(["reload", "back_forward"]);

function encodeBase64Url(text) {
  if (!text) return "";
  try {
    return window
      .btoa(unescape(encodeURIComponent(text)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");
  } catch {
    return "";
  }
}

function decodeBase64Url(text) {
  const input = String(text || "").trim();
  if (!input) return "";
  try {
    const padded = input.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(input.length / 4) * 4, "=");
    return decodeURIComponent(escape(window.atob(padded)));
  } catch {
    return "";
  }
}

function cleanText(value) {
  const text = String(value || "").trim();
  return text || "";
}

function publicPatientDisplay(input = {}) {
  const patient = input?.patient || {};
  const careSubjectType = cleanText(
    patient?.careSubjectType || input?.careSubjectType || input?.care_subject_type,
  ).toLowerCase();
  if (careSubjectType === "dependant") return "Dependant patient";

  const name = cleanText(patient?.name || input?.patientName);
  const parts = name.split(/\s+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((part) => part.slice(0, 1).toUpperCase())
    .join("");
  return initials || "Patient";
}

export function buildPrescriptionVerifySnapshot(input = {}) {
  const patient = input?.patient || {};
  const doctor = input?.doctor || {};
  const facility = doctor?.facility || input?.facility || {};
  const signatureImage = cleanText(input?.signature_image || doctor?.signatureSrc || input?.signatureImage);
  const nextSignatureImage =
    signatureImage && !/^data:/i.test(signatureImage) && signatureImage.length <= 240 ? signatureImage : "";

  return {
    status: cleanText(input?.status) || "issued",
    is_signed: Boolean(input?.signed_at || input?.isSigned || nextSignatureImage),
    issued_at: cleanText(input?.issuedAt || input?.issued_at),
    signed_at: cleanText(input?.signedAt || input?.signed_at),
    expires_at: cleanText(input?.expiresAt || input?.expires_at),
    digital_seal: cleanText(input?.digitalSeal || input?.digital_seal),
    signature_image: nextSignatureImage,
    rxNo: cleanText(input?.rxNo),
    medicineName: cleanText(input?.medicineName),
    form: cleanText(input?.form),
    ingredients: cleanText(input?.ingredients),
    strengthValue: cleanText(input?.strengthValue),
    strengthUnit: cleanText(input?.strengthUnit),
    strengthLabel: cleanText(input?.strengthLabel),
    frequencyLabel: cleanText(input?.frequencyLabel),
    dosageLabel: cleanText(input?.dosageLabel),
    dosesPerTime: cleanText(input?.dosesPerTime || input?.schedule?.dosesPerTime),
    doseUnitLabel: cleanText(input?.doseUnitLabel || input?.schedule?.doseUnitLabel),
    startsOn: cleanText(input?.startsOn || input?.schedule?.startDateIso),
    endsOn: cleanText(input?.endsOn || input?.schedule?.endDateIso),
    doctorPrescriptionName: cleanText(doctor?.prescriptionName || input?.doctorPrescriptionName),
    doctorName: cleanText(doctor?.name || input?.doctorName),
    doctorLicenseNumber: cleanText(doctor?.regNo || input?.doctorLicenseNumber),
    facilityName: cleanText(facility?.name || input?.facilityName),
    patientDisplay: publicPatientDisplay(input),
  };
}

export function encodePrescriptionVerifySnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return "";
  const compact = Object.fromEntries(
    Object.entries(snapshot).filter(([, value]) => value !== null && value !== undefined && value !== "")
  );
  return encodeBase64Url(JSON.stringify(compact));
}

export function decodePrescriptionVerifySnapshot(encoded) {
  const json = decodeBase64Url(encoded);
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function isPrescriptionUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

export function extractPrescriptionUuid(input) {
  const text = String(input || "").trim();
  if (!text) return "";

  if (isPrescriptionUuid(text)) return text;

  const match = text.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i
  );
  if (match?.[0] && isPrescriptionUuid(match[0])) return match[0];

  try {
    const parsed = JSON.parse(text);
    const id = parsed?.id || parsed?.rx_id || parsed?.rxId || parsed?.prescriptionId;
    return isPrescriptionUuid(id) ? String(id).trim() : "";
  } catch {
    return "";
  }
}

export function buildPrescriptionVerifyPath(prescriptionId) {
  const id = String(prescriptionId || "").trim();
  if (!isPrescriptionUuid(id)) return "";
  return `${VERIFY_ROUTE_PREFIX}/${encodeURIComponent(id)}`;
}

export function buildPrescriptionVerifyUrl(prescriptionId, origin = "", snapshot = null) {
  const path = buildPrescriptionVerifyPath(prescriptionId);
  if (!path) return "";
  const runtimeBase = String(origin || "").trim().replace(/\/+$/, "");
  const envBase = String(
    import.meta.env.VITE_APP_URL || import.meta.env.VITE_SITE_URL || ""
  )
    .trim()
    .replace(/\/+$/, "");
  const prefersEnvBase =
    /^https?:\/\/(?:localhost|127(?:\.\d{1,3}){3}|0\.0\.0\.0)(?::\d+)?$/i.test(runtimeBase);
  const base = prefersEnvBase
    ? envBase || runtimeBase
    : runtimeBase || envBase;
  const encodedSnapshot = encodePrescriptionVerifySnapshot(snapshot);
  const search = encodedSnapshot ? `?snapshot=${encodeURIComponent(encodedSnapshot)}` : "";
  return base ? `${base}${path}${search}` : `${path}${search}`;
}

export function buildPrescriptionVerifyState({
  from = "",
  actorRole = "",
  prescriptionId = "",
  rawText = "",
  entryMethod = "",
  returnTo = "",
  returnState = null,
  snapshot = null,
} = {}) {
  const nextPrescriptionId = extractPrescriptionUuid(prescriptionId || rawText);

  return {
    from: String(from || "").trim(),
    actorRole: String(actorRole || "").trim(),
    prescriptionId: nextPrescriptionId || null,
    rawText: String(rawText || "").trim(),
    entryMethod: String(entryMethod || "").trim(),
    returnTo: String(returnTo || "").trim(),
    returnState: returnState || null,
    snapshot: snapshot && typeof snapshot === "object" ? snapshot : null,
    savedAt: new Date().toISOString(),
  };
}

export function savePrescriptionVerifyContext(context) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(VERIFY_CONTEXT_KEY, JSON.stringify(context || null));
  } catch {
    // ignore storage errors
  }
}

export function readPrescriptionVerifyContext(expectedId = "") {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(VERIFY_CONTEXT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const nextId = extractPrescriptionUuid(parsed?.prescriptionId || parsed?.rawText);
    const requiredId = extractPrescriptionUuid(expectedId);

    if (requiredId && requiredId !== nextId) return null;

    return {
      ...parsed,
      prescriptionId: nextId || null,
    };
  } catch {
    return null;
  }
}

export function shouldRestorePrescriptionVerifyContext() {
  if (typeof window === "undefined") return false;

  try {
    const [entry] = window.performance?.getEntriesByType?.("navigation") || [];
    const navigationType = String(entry?.type || "").trim().toLowerCase();
    return RESTORABLE_NAVIGATION_TYPES.has(navigationType);
  } catch {
    return false;
  }
}
